import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TourismGuideFileMultimediaTypesService } from '@/tourist-guides/tourism-guides-file/_services/tourism-guide-file-multimedia-types.service';

export type DocumentTypeDialogData = {
	selectedId: number | null;
};

export type DocumentTypeDialogResult = {
	documentTypeId: number | null;
};

@Component({
	selector: 'app-document-type-dialog',
	standalone: true,
	imports: [FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule],
	template: `
		<h2 mat-dialog-title>Typ dokumentu</h2>
		<div mat-dialog-content>
			<mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-100">
				<mat-label>Typ dokumentu</mat-label>
				<mat-select
					[value]="selectedId()"
					[disabled]="documentTypesService.isLoading()"
					(selectionChange)="selectedId.set($event.value)"
				>
					<mat-option [value]="null"></mat-option>
					@for (type of documentTypesService.documentTypes(); track type.id) {
						<mat-option [value]="type.id">{{ type.name }}</mat-option>
					}
				</mat-select>
			</mat-form-field>

			@if (isAddingNewType()) {
				<div class="d-flex align-items-start gap-2 mt-2">
					<mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-grow-1">
						<mat-label>Nazwa nowego typu</mat-label>
						<input matInput [ngModel]="newTypeName()" (ngModelChange)="newTypeName.set($event)" />
					</mat-form-field>
					<button
						mat-icon-button
						type="button"
						[disabled]="documentTypesService.isCreating() || !newTypeName().trim()"
						(click)="confirmAddType()"
						aria-label="Zapisz typ dokumentu"
					>
						<i class="fa-solid fa-check"></i>
					</button>
					<button
						mat-icon-button
						type="button"
						[disabled]="documentTypesService.isCreating()"
						(click)="cancelAddType()"
						aria-label="Anuluj dodawanie typu"
					>
						<i class="fa-solid fa-xmark"></i>
					</button>
				</div>
			} @else {
				<button
					mat-stroked-button
					type="button"
					class="mt-2"
					[disabled]="documentTypesService.isLoading()"
					(click)="onAddTypeClick()"
				>
					<i class="fa-solid fa-plus me-1"></i>Nowy typ
				</button>
			}
		</div>

		<div mat-dialog-actions align="end">
			<button mat-button type="button" (click)="cancel()">Anuluj</button>
			<button mat-raised-button color="primary" type="button" (click)="confirm()">Zapisz</button>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentTypeDialogComponent {
	readonly data = inject<DocumentTypeDialogData>(MAT_DIALOG_DATA);
	readonly documentTypesService = inject(TourismGuideFileMultimediaTypesService);

	readonly selectedId = signal<number | null>(this.data.selectedId);
	readonly isAddingNewType = signal(false);
	readonly newTypeName = signal('');

	private readonly destroyRef = inject(DestroyRef);
	private readonly dialogRef =
		inject<MatDialogRef<DocumentTypeDialogComponent, DocumentTypeDialogResult>>(MatDialogRef);

	confirm(): void {
		this.dialogRef.close({ documentTypeId: this.selectedId() });
	}

	cancel(): void {
		this.dialogRef.close();
	}

	onAddTypeClick(): void {
		this.isAddingNewType.set(true);
	}

	confirmAddType(): void {
		const name = this.newTypeName().trim();
		if (!name) {
			return;
		}
		this.documentTypesService
			.create(name)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((documentType) => {
				this.selectedId.set(documentType.id);
				this.isAddingNewType.set(false);
				this.newTypeName.set('');
			});
	}

	cancelAddType(): void {
		this.isAddingNewType.set(false);
		this.newTypeName.set('');
	}
}
